import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow
} from "src/components/ui/table";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
	useReactTable,
	flexRender,
	getCoreRowModel,
	SortingState,
	getSortedRowModel
} from "@tanstack/react-table";
import { useState } from "react";
import dayjs from "dayjs";
import getRemainingMonthsInYear from "../../utils/getRemainingMonthsInYear/getRemainingMonthsInYear";
import EmptyState from "src/components/EmptyState/EmptyState";

type DataTableProps<Data extends object> = {
	data: Data[];
	columns: any;
	onFormSelect?: (id: number, month: string) => void;
	calendarMonth?: Month;
	calendarYear?: number;
	observationType?: IObservationType;
	emptyDataMessage: string;
	onRowClick?: (row: Data) => void;
};

const DataTable = <Data extends object>({
	data,
	columns,
	calendarYear,
	observationType,
	emptyDataMessage,
	onRowClick
}: DataTableProps<Data>) => {
	const [sorting, setSorting] = useState<SortingState>([]);

	const { getHeaderGroups, getRowModel, getFooterGroups } = useReactTable({
		columns,
		data,
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting
		}
	});

	const renderRows = () => {
		if (getRowModel().rows.length === 0) {
			return (
				<TableRow>
					<TableCell colSpan={getHeaderGroups()[0].headers.length}>
						<EmptyState title={emptyDataMessage} backgroundColor="#fdf2f0" />
					</TableCell>
				</TableRow>
			);
		}

		return getRowModel().rows.map((row: any) => {
			return (
				<TableRow
					key={row.id}
					className={`group/row hover:bg-primary-lighter/50 ${onRowClick ? "cursor-pointer" : ""}`}
					onClick={(e) => {
						const target = e.target as HTMLElement | null;
						if (
							target &&
							target.closest(
								"button, a, input, textarea, select, [role='button'], [role='link']"
							)
						) {
							return;
						}
						onRowClick?.(row.original);
					}}
					tabIndex={onRowClick ? 0 : undefined}
					onKeyDown={
						onRowClick
							? (e) => {
									if (e.key === "Enter" || e.key === " ") {
										const target = e.target as HTMLElement | null;
										if (
											target &&
											target.closest(
												"button, a, input, textarea, select, [role='button'], [role='link']"
											)
										) {
											return;
										}
										e.preventDefault();
										onRowClick(row.original);
									}
								}
							: undefined
					}
				>
					{row.getVisibleCells().map((cell: any, index: number) => {
						const rowMonth = cell.getContext().column.id;
						const currentDate = dayjs();

						const lastDayOfYear =
							calendarYear && dayjs().year(calendarYear).endOf("year");

						const showBlankRow =
							!currentDate.isAfter(lastDayOfYear) &&
							observationType === "GREAT_CONVERSATION" &&
							getRemainingMonthsInYear(currentDate).includes(rowMonth);

						return (
							<TableCell
								key={cell.id}
								className={
									index === 0
										? "text-left sticky left-0 bg-inherit font-medium text-foreground h-14"
										: "text-center text-zinc-600 h-14"
								}
							>
								{showBlankRow ? (
									<span className="text-zinc-300">&mdash;</span>
								) : (
									<>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</>
								)}
							</TableCell>
						);
					})}
				</TableRow>
			);
		});
	};

	const renderColumns = () => {
		return getHeaderGroups().map((headerGroup) => (
			<TableRow
				key={headerGroup.id}
				className="bg-zinc-50 border-b-2 border-border"
			>
				{headerGroup.headers.map((header, index) => {
					return (
						<TableHead
							key={header.id}
							onClick={header.column.getToggleSortingHandler()}
							className={`capitalize cursor-pointer text-xs font-semibold uppercase tracking-wider text-zinc-500 ${index === 0 ? "text-left" : "text-center"}`}
						>
							<span className="inline-flex items-center gap-1">
								{flexRender(
									header.column.columnDef.header,
									header.getContext()
								)}

								{header.column.getIsSorted() ? (
									header.column.getIsSorted() === "desc" ? (
										<ChevronDown
											className="w-3 h-3"
											aria-label="sorted descending"
										/>
									) : (
										<ChevronUp
											className="w-3 h-3"
											aria-label="sorted ascending"
										/>
									)
								) : null}
							</span>
						</TableHead>
					);
				})}
			</TableRow>
		));
	};

	return (
		<div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
			<Table>
				<TableHeader>{renderColumns()}</TableHeader>

				<TableBody>{renderRows()}</TableBody>

				<TableFooter>
					{getFooterGroups().map((footerGroup) => (
						<TableRow key={footerGroup.id}>
							{footerGroup.headers.map((header) => (
								<TableHead key={header.id} className="font-bold normal-case">
									{!header.isPlaceholder &&
										flexRender(
											header.column.columnDef.footer,
											header.getContext()
										)}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableFooter>
			</Table>
		</div>
	);
};

export default DataTable;
